import { useState, useEffect } from 'react';

export interface FavoriteQuestion {
  id: number;
  question_text: string;
  options: string[];
  correct_answer: string;
  explanation?: string;
  stem?: string;
  reference?: string;
  subject_id?: string;
  subject_name?: string;
  chapter_id?: string;
}

// Global set of listeners to sync state across all hook instances
const listeners = new Set<(favs: FavoriteQuestion[]) => void>();

let globalFavorites: FavoriteQuestion[] = (() => {
  try {
    const stored = localStorage.getItem('chorcaghor_favorites');
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
})();

const saveFavorites = (favs: FavoriteQuestion[]) => {
  globalFavorites = favs;
  localStorage.setItem('chorcaghor_favorites', JSON.stringify(favs));
  listeners.forEach(listener => listener(favs));
};

export const useFavorites = () => {
  const shAuth = (window as any).shAuth;
  const [favorites, setFavorites] = useState<FavoriteQuestion[]>(globalFavorites);

  useEffect(() => {
    const handleUpdate = (updatedFavs: FavoriteQuestion[]) => {
      setFavorites(updatedFavs);
    };
    listeners.add(handleUpdate);
    return () => {
      listeners.delete(handleUpdate);
    };
  }, []);

  const addToFavorites = (question: FavoriteQuestion) => {
    if (globalFavorites.some(q => q.id === question.id)) return;
    const updated = [...globalFavorites, question];
    saveFavorites(updated);

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (shAuth?.rest_nonce) {
      headers['X-WP-Nonce'] = shAuth.rest_nonce;
    }

    fetch('/wp-json/shuddhoshikha/v1/sync-history', {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({
        action: 'toggle_save',
        question_id: question.id,
        save: true
      }),
      keepalive: true
    }).catch(err => console.error('Failed to sync favorite add:', err));
  };

  const removeFromFavorites = (questionId: number) => {
    const updated = globalFavorites.filter(q => q.id !== questionId);
    saveFavorites(updated);

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (shAuth?.rest_nonce) {
      headers['X-WP-Nonce'] = shAuth.rest_nonce;
    }

    fetch('/wp-json/shuddhoshikha/v1/sync-history', {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({
        action: 'toggle_save',
        question_id: questionId,
        save: false
      }),
      keepalive: true
    }).catch(err => console.error('Failed to sync favorite remove:', err));
  };

  const isFavorite = (questionId: number) => {
    return favorites.some(q => q.id === questionId);
  };

  const toggleFavorite = (question: FavoriteQuestion) => {
    if (isFavorite(question.id)) {
      removeFromFavorites(question.id);
    } else {
      addToFavorites(question);
    }
  };

  return {
    favorites,
    addToFavorites,
    removeFromFavorites,
    isFavorite,
    toggleFavorite,
  };
};
